import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';
import { format, eachDayOfInterval, startOfWeek, addDays } from 'date-fns';

import Month from './month';
import { formatMoney, monthLabelToNumber } from '@spa/shared/utils';
import {
  DayPickerContainer,
  MonthSelector,
  MonthLabel,
  MonthPrice,
  TableRangeMonths,
  TableRangeMonth,
  WeekContainer,
  WeekDayContainer
} from './styles';
import { useMonths } from '@spa/shared/hooks/dateRange';
import Button from '@spa/shared/components/Search/Button';
import { DATE_PICKER_SUPPORTED_LOCALES } from '@spa/shared/constants/date-picker-supported-locales';

export const MoveInOutPickerMobile = ({ dateRange, classNames = {}, rents = [] }) => {
  const {
    t,
    i18n: { language }
  } = useTranslation();
  const { activeMonths, firstDayOfWeek, goToPreviousMonthsByOneMonth } = dateRange;
  const [visibleMonths, setVisibleMonths] = useState(4);

  const currentLocale = DATE_PICKER_SUPPORTED_LOCALES[language] || undefined;

  const months = useMonths(activeMonths, firstDayOfWeek);
  const weekdayLabels = eachDayOfInterval({
    start: startOfWeek(new Date(), { weekStartsOn: 0 }),
    end: addDays(startOfWeek(new Date(), { weekStartsOn: 0 }), 6)
  }).map(day => t(format(day, 'EEE', { locale: currentLocale })));

  const showMoreMonths = () => setVisibleMonths(prevVisible => Math.min(prevVisible + 4, activeMonths.length));

  const className = Object.keys(classNames)
    .filter(name => classNames[name])
    .join(' ');

  return (
    <DayPickerContainer className={className}>
      <WeekContainer>
        {weekdayLabels.map((weekDay, i) => (
          <WeekDayContainer key={i}>{weekDay}</WeekDayContainer>
        ))}
      </WeekContainer>
      <TableRangeMonths>
        <Button isTertiary onClick={goToPreviousMonthsByOneMonth}>
          {t('Show previous dates')}
        </Button>
        {activeMonths.slice(0, visibleMonths).map((month, index) => {
          const monthLabel = months[index].monthLabel;
          const monthNumber = monthLabelToNumber(monthLabel, language);
          const rent = rents[monthNumber];

          return (
            <React.Fragment key={`${month.year}-${month.month}`}>
              <MonthSelector>
                <MonthLabel>
                  {`${monthLabel} `}
                  <span>{months[index].year}</span>
                </MonthLabel>
                {rent ? (
                  <MonthPrice>
                    {formatMoney(rent.amount, rent.currency_code)}/{t('month')}
                  </MonthPrice>
                ) : null}
              </MonthSelector>
              <TableRangeMonth>
                <Month year={month.year} month={month.month} dateRange={dateRange} />
              </TableRangeMonth>
            </React.Fragment>
          );
        })}
        {visibleMonths < activeMonths.length && (
          <Button isTertiary onClick={showMoreMonths}>
            {t('Show more dates')}
          </Button>
        )}
      </TableRangeMonths>
    </DayPickerContainer>
  );
};

MoveInOutPickerMobile.propTypes = {
  dateRange: PropTypes.object.isRequired,
  classNames: PropTypes.object,
  rents: PropTypes.array
};

export default MoveInOutPickerMobile;
